import React, { useEffect, useRef, useState } from 'react';

interface CommentItem {
  id: string;
  author_name: string;
  author_avatar: string | null;
  content: string;
  created_at: string;
  is_featured: boolean;
}

interface PaginatedComments {
  count: number;
  next: string | null;
  previous: string | null;
  results: CommentItem[];
}

interface CommentsProps {
  articleSlug: string;
  allowComments: boolean;
}

const Comments: React.FC<CommentsProps> = ({ articleSlug, allowComments }) => {
  const containerRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [content, setContent] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  // Lazy-load: defer fetch until the section scrolls near the viewport
  useEffect(() => {
    if (!containerRef.current || visible) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  // Fetch once visible
  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    fetch(`/api/articles/${articleSlug}/comments/`)
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed');
        return res.json();
      })
      .then((data: PaginatedComments) => {
        setComments(data.results || []);
        setCount(data.count || 0);
        setError(null);
      })
      .catch(() => setError('Could not load comments.'))
      .finally(() => setLoading(false));
  }, [visible, articleSlug]);

  const submitDisabled = !content.trim() || submitStatus === 'submitting' || submitStatus === 'success';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabled) return;
    setSubmitStatus('submitting');
    setSubmitMessage('');
    try {
      const res = await fetch(`/api/articles/${articleSlug}/comments/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: content.trim(),
          guest_name: name.trim(),
          guest_email: email.trim(),
          honeypot: website,
        }),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setSubmitMessage('Thanks! Your comment will appear after moderation.');
        setContent('');
        setName('');
        setEmail('');
        return;
      }
      const data = await res.json().catch(() => ({}));
      const detail =
        data.detail ||
        (Array.isArray(data.content) && data.content[0]) ||
        (Array.isArray(data.honeypot) && 'Spam detected.') ||
        (Array.isArray(data.guest_email) && data.guest_email[0]) ||
        'Something went wrong.';
      setSubmitStatus('error');
      setSubmitMessage(detail);
    } catch {
      setSubmitStatus('error');
      setSubmitMessage('Network error. Try again.');
    }
  };

  if (!allowComments) return null;

  return (
    <section ref={containerRef} className="bg-white border-t border-slate-200 py-10">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-xl font-bold text-slate-900 mb-1">
          Comments {count > 0 && <span className="text-slate-500 font-normal">({count})</span>}
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          Share your thoughts. Comments are moderated before publishing.
        </p>

        {visible && loading && <p className="text-sm text-slate-500">Loading…</p>}
        {visible && !loading && error && <p className="text-sm text-red-600">{error}</p>}
        {visible && !loading && !error && comments.length === 0 && (
          <p className="text-sm text-slate-500 mb-8">Be the first to comment.</p>
        )}

        {visible && !loading && comments.length > 0 && (
          <ul className="space-y-5 mb-8">
            {comments.map((c) => (
              <li key={c.id} className="flex gap-3">
                {c.author_avatar ? (
                  <img
                    src={c.author_avatar}
                    alt={c.author_name}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-sm font-semibold flex-shrink-0">
                    {c.author_name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline flex-wrap gap-x-2 mb-0.5">
                    <span className="text-sm font-medium text-slate-900">{c.author_name}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(c.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                    {c.is_featured && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{c.content}</p>
                </div>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={handleSubmit} className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Leave a comment</h3>
          {/* Labels, not placeholders. The placeholders carry real instructions —
              "optional", "not published" — and a placeholder disappears on the
              first keystroke, so the one piece of information a reader needs while
              typing is the piece that vanishes. Visually hidden to keep the
              compact layout; announced by screen readers either way. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <label htmlFor="comment-name" className="sr-only">Name (optional)</label>
            <input
              id="comment-name"
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitStatus === 'submitting' || submitStatus === 'success'}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm disabled:bg-slate-100"
            />
            <label htmlFor="comment-email" className="sr-only">Email (optional, not published)</label>
            <input
              id="comment-email"
              type="email"
              placeholder="Email (optional, not published)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitStatus === 'submitting' || submitStatus === 'success'}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm disabled:bg-slate-100"
            />
          </div>
          <label htmlFor="comment-body" className="sr-only">Your comment</label>
          <textarea
            id="comment-body"
            placeholder="Your comment…"
            required
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={submitStatus === 'submitting' || submitStatus === 'success'}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm disabled:bg-slate-100 mb-3"
          />
          {/* Honeypot — invisible to real users */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            aria-hidden="true"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="submit"
              disabled={submitDisabled}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitStatus === 'submitting' ? 'Posting…' : submitStatus === 'success' ? 'Submitted' : 'Post comment'}
            </button>
            {submitMessage && (
              <p
                role={submitStatus === 'error' ? 'alert' : 'status'}
                className={`text-sm ${submitStatus === 'success' ? 'text-emerald-700' : 'text-red-700'}`}
              >
                {submitMessage}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default Comments;
